import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { downloadCompressed, discardSessionRequest, generateSessionId, submitFileJob } from '../utils/pdfUtils';

export const PdfRenamerForm = () => {
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => { document.title = 'OptiConfig PDF Renamer'; }, []);

  const toast = useRef(null);

  const showError = (err) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: err, life: 3000 });
  };

  const showSuccess = (msg) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
  };

  const [token, setToken] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId());
  const [succeeded, setSucceeded] = useState(false);

  // Progress dialog state for chunked processing
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressDialogVisible, setProgressDialogVisible] = useState(false);
  const [errorFiles, setErrorFiles] = useState([]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const accessToken = await fetchAccessToken(getAccessTokenSilently);
        setToken(accessToken);
      } catch (error) {
        console.error('Error fetching access token:', error);
        showError('Failed to fetch access token. Please try again.');
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!uploadedFiles || uploadedFiles.length === 0) {
      showError('Please upload at least one PDF file.');
      return;
    }

    setSubmitting(true);

    const fileNames = uploadedFiles.map((file) => file.name);

    // Chunking
    const chunkSize = 3;
    const totalFiles = fileNames.length;

    try {
      // Init and show progress
      setProgress({ processed: 0, total: totalFiles });
      setErrorFiles([]);
      setIsProcessing(true);
      setProgressDialogVisible(true);

      const failedFiles = [];

      for (let i = 0; i < totalFiles; i += chunkSize) {
        const chunk = fileNames.slice(i, i + chunkSize);
        try {
          const result = await submitFileJob('/pdf/rename', sessionId, token, chunk);
          // Files that errored server-side (e.g. no VIN found) don't throw,
          // they come back as per-file entries in `errors`.
          const chunkErrors = Object.keys(result?.errors || {});
          if (chunkErrors.length) {
            failedFiles.push(...chunkErrors);
            setErrorFiles((prev) => [...prev, ...chunkErrors]);
          }
        } catch (err) {
          // mark this chunk's files as failed
          failedFiles.push(...chunk);
          setErrorFiles((prev) => [...prev, ...chunk]);
        }

        setProgress((prev) => ({
          processed: Math.min(prev.processed + chunk.length, prev.total),
          total: prev.total,
        }));
      }

      if (failedFiles.length >= totalFiles) {
        // Every file failed to process - there is nothing to download.
        setSucceeded(false);
        showError('All files failed to process. See errors for details.');
      } else {
        setSucceeded(true);
        showSuccess('Files processed successfully');
      }
    } catch (error) {
      showError(error.message || 'Submission failed.');
    } finally {
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  const discardSession = async (e) => {
    e?.preventDefault();
    try {
      await discardSessionRequest(sessionId, token);
    } catch (err) {
      showError(err.message || 'Failed to discard session on server.');
    } finally {
      setUploadedFiles([]);
      setSucceeded(false);
      setSessionId(generateSessionId());
      setProgress({ processed: 0, total: 0 });
      setErrorFiles([]);
      setIsProcessing(false);
      setProgressDialogVisible(false);
    }
  };

  const onDownload = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      await downloadCompressed(sessionId, token);
      showSuccess('File downloaded successfully');
      await discardSession();
    } catch (error) {
      showError(error.message || 'Download failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
      <Toast ref={toast} />

      {/* Progress Dialog */}
      <Dialog
        header="Renaming Progress"
        visible={progressDialogVisible}
        style={{ width: '75vw', maxWidth: '1200px' }}
        onHide={() => setProgressDialogVisible(false)}
        closable={!isProcessing}
        dismissableMask={!isProcessing}
        draggable={false}
        resizable={false}
        modal
      >
        <div className="p-fluid" style={{ fontSize: '0.8rem', lineHeight: 1.25 }}>
          <div className="field">
            <strong>Progress:</strong>
            <div style={{ marginTop: 8 }}>
              <ProgressBar
                value={progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}
              />
              <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                {isProcessing
                  ? `Processing ${progress.processed} / ${progress.total} files...`
                  : progress.total > 0
                  ? `Processed ${progress.processed} / ${progress.total} files`
                  : 'Idle'}
              </div>
            </div>
          </div>
          {errorFiles.length > 0 && (
            <div className="field">
              <strong>Errors:</strong>
              <ul
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  paddingLeft: 16,
                  fontSize: '0.75rem',
                  color: '#b00020',
                }}
              >
                {errorFiles.map((fname, idx) => (
                  <li key={`err-${fname}-${idx}`}>{fname}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>
      <div className="col-12">
        <div className="grid formgrid p-fluid">
          <div className="field col-12">
            <h4 style={{ textAlign: 'center' }}>PDF VIN Renamer Tool</h4>
          </div>
          <div className="field col-4"></div>
          <div className="field col-4" style={{ display: 'flex', justifyContent: 'center' }}>
            {!succeeded && (
              <Button
                type="submit"
                label={submitting || isProcessing ? 'Submitting...' : 'Submit'}
                className="p-button-primary"
                style={{ maxWidth: '300px', width: '100%' }}
                disabled={uploadedFiles.length === 0 || submitting || isProcessing}
              />
            )}
            {succeeded && (
              <>
                <Button
                  onClick={onDownload}
                  label={submitting ? 'Downloading...' : 'Download .zip'}
                  className="p-button-primary"
                  style={{ width: '50%' }}
                  disabled={uploadedFiles.length === 0 || submitting || isProcessing}
                />
              </>
            )}
            {uploadedFiles.length > 0 && (
              <Button
                onClick={discardSession}
                label={'Discard'}
                className="p-button-secondary"
                style={{ width: '50%', marginLeft: 20 }}
                disabled={submitting || isProcessing}
              />
            )}
          </div>
          <div className="field col-4"></div>
          <div className="field col-3"></div>
          <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
            <CustomFileUpload
              acceptedFileTypes={['application/pdf', '.pdf']}
              onMessage={(msg) => {
                showError(msg.detail);
              }}
              token={token}
              fileValue={uploadedFiles}
              sessionId={sessionId}
              onChangeFiles={(files) => {
                setSucceeded(false);
                setUploadedFiles(files);
              }}
            />
          </div>
        </div>
      </div>
    </form>
  );
};
